/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ChangeEvent, useCallback} from 'react';
import {setViewSubGroupsState} from '@ion/applications/src/utils/helpers';

export interface useToggleSubGroupChangeProps {
  onChecked?: Function;
  onChange?: Function;
  setSubAccountChecked?: Function;
  preference?: string;
}

/**
 * Defines the useToggleSubGroupChange component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * const onToggleSubGroups = useToggleSubGroupChange(onChecked, onChange);
 * 
 * @param  onChecked object will take precedence when passed as an param.
 * @param  onChange object will take lower precedence over onChecked and 
 *         need to pass checked an boolean param to it.
 *
 * ```
 * 
 */
export const useToggleSubGroupChange = ({
  onChecked,
  onChange,
  setSubAccountChecked,
  preference = ''
}) => (
  useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (onChecked) {
      onChecked(checked);
    } else {
      onChange?.(e, checked);
    }

    setSubAccountChecked?.(checked);
    
    if (preference) {
      setViewSubGroupsState(preference, checked);
    }
  }, [onChecked, onChange, setSubAccountChecked, preference])
);

export default useToggleSubGroupChange;
